'use client';

/**
 * Parses a MFour crosstab Excel file into a shape compatible with
 * CrosstabDataResponse (minus `responses` and `isInternalUser`).
 * The result is fed directly into buildCrosstabContext so DANI receives
 * the same structured question/option data it gets from the API.
 *
 * Handles the two most common MFour export layouts:
 *  - Simple: Q-ID + question text in col A, answer options in subsequent rows
 *  - Multi-sheet: same layout across multiple worksheets
 *
 * Heuristics used (in order):
 *  1. Any row whose first cell starts with Q/S/D + digits is a question header.
 *  2. Rows immediately following a question header are options (until the next
 *     question header or a numeric-heavy row that looks like a count/pct row).
 *  3. Rows named "Base", "Unweighted Base", "Total Respondents", or matching
 *     /^n\s*=\s*\d+/i are used to extract totalRespondents.
 */

import type { CrosstabDataResponse, CrosstabQuestion } from '@/lib/crosstab/data-types';
import {
    isExtraTableTitle,
    isFootnoteText,
    isIndexSheetName,
    isNetsSheetName,
    isUnweightedSheetName,
    isVerbatimSheetName,
} from '@/lib/crosstab/summary-tables';
import { displayOfNumber } from '@/lib/crosstab/xlsx-number-format';
import ExcelJS from 'exceljs';

const Q_ID_RE = /^([QqSsDdBb]\d+[a-zA-Z]?)\b[.\s:]*(.*)/;
// M4S-5777 (X87): a weighted export's "Effective base" is a base row, never an answer.
const BASE_RE =
    /^(base|unweighted\s+base|effective\s+base|total\s+respondents|respondents|n\s*=\s*\d+)/i;

type RawRow = (string | number | null)[];

function cellText(val: ExcelJS.CellValue, numFmt?: string): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val.trim();
    // A number reads as the text its format shows, as exports wrote it before M4A2A-570.
    if (typeof val === 'number') return displayOfNumber(val, numFmt);
    if (typeof val === 'object') {
        // Formula cell
        if ('result' in val) return cellText(val.result as ExcelJS.CellValue, numFmt);
        // Rich text
        if ('richText' in val && Array.isArray((val as ExcelJS.CellRichTextValue).richText)) {
            return (val as ExcelJS.CellRichTextValue).richText
                .map((r) => r.text)
                .join('')
                .trim();
        }
    }
    return String(val);
}

function sheetToRows(sheet: ExcelJS.Worksheet): RawRow[] {
    const rows: RawRow[] = [];
    sheet.eachRow((row) => {
        const cells: RawRow = [];
        row.eachCell({ includeEmpty: true }, (cell) => {
            cells.push(cellText(cell.value, cell.numFmt) || null);
        });
        // Trim trailing nulls
        while (cells.length && cells[cells.length - 1] === null) cells.pop();
        if (cells.some((c) => c !== null)) rows.push(cells);
    });
    return rows;
}

/** Returns true when the majority of non-first cells in a row are numeric — looks like a count/pct row. */
function isDataRow(row: RawRow): boolean {
    const tail = row.slice(1).filter((c) => c !== null && c !== '');
    if (tail.length < 2) return false;
    const numCount = tail.filter((c) => !isNaN(Number(c))).length;
    return numCount / tail.length > 0.55;
}

function extractFromRows(
    rows: RawRow[],
    questions: CrosstabQuestion[],
    totalRespondents: { value: number },
    sortOrderBase: number,
): void {
    let currentQ: { id: string; question: string; options: string[] } | null = null;
    let currentSortOrder = sortOrderBase;

    const flush = () => {
        if (currentQ && currentQ.options.length > 0) {
            questions.push({
                id: currentQ.id,
                qid: currentQ.id,
                question: currentQ.question,
                sortOrder: currentSortOrder++,
                questionType: 'single',
                min: null,
                max: null,
                matrixOptions: {},
                options: currentQ.options.map((opt, i) => ({
                    option_id: [`${currentQ!.id}_${i}`],
                    sort_order: i,
                    option: opt,
                })),
            });
        }
        currentQ = null;
    };

    for (const row of rows) {
        const first = String(row[0] ?? '').trim();
        if (!first) continue;
        // M4S-5777 (X104): a table's footnote row is a note, never an answer.
        if (isFootnoteText(first)) continue;

        // Detect base/respondent rows
        if (BASE_RE.test(first)) {
            // The "Total" column is usually col index 1; take the largest number seen
            for (let i = 1; i < row.length; i++) {
                const n = Number(row[i]);
                if (!isNaN(n) && n > 0 && n > totalRespondents.value) {
                    totalRespondents.value = n;
                }
            }
            continue;
        }

        // A Top 2 / Bottom 2 summary table (M4S-5592) lists the matrix's statements, and the reader
        // keeps the first table it meets for a question, so it would hand DANI the statements as
        // the question's answers. A ranked question's Rank 2… and Full Rank tables (M4S-5777) share
        // its code too. Read nothing until the next question header.
        if (isExtraTableTitle(first)) {
            flush();
            continue;
        }

        // Detect question header rows
        const qMatch = first.match(Q_ID_RE);
        if (qMatch) {
            flush();
            const id = qMatch[1].toUpperCase();
            // Question text might be in the same cell or in col 1
            const inlineText = qMatch[2].trim();
            const col1Text = row[1] != null ? String(row[1]).trim() : '';
            const question = inlineText || col1Text || id;
            currentQ = { id, question, options: [] };
            continue;
        }

        // Collect answer options for current question
        if (currentQ) {
            // Skip if it's a numeric data row (count/pct values)
            if (!isDataRow(row)) {
                // Skip header-like rows (all-caps short labels that look like column headers)
                const isHeader =
                    first.length <= 8 && first === first.toUpperCase() && !/\s/.test(first);
                if (!isHeader) {
                    currentQ.options.push(first);
                }
            }
        }
    }

    flush();
}

export async function excelToCrosstabData(
    file: File,
): Promise<Omit<CrosstabDataResponse, 'responses'>> {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const questions: CrosstabQuestion[] = [];
    const totalRespondents = { value: 0 };

    // Skip sheets that look like a table-of-contents index (very few data rows), and the export's
    // net-and-mean sheet under either name (M4S-5592): it holds only each question's net rows, and
    // exports made before it moved last put it ahead of the data, so a matrix's options were that
    // net alone. The "Summary" cover sheet stays: its "Respondents (n)" row is the only source of
    // totalRespondents here, since a bare "Total" row does not match BASE_RE.
    const dataSheets = workbook.worksheets.filter((sheet) => {
        if (isIndexSheetName(sheet.name) || isNetsSheetName(sheet.name)) return false;
        // M4S-5777 (X90): the unweighted set repeats every question; the weighted tabs are read.
        if (isUnweightedSheetName(sheet.name)) return false;
        // M4S-5777 (X112): the verbatims are text, not a table.
        if (isVerbatimSheetName(sheet.name)) return false;
        return sheet.rowCount > 2;
    });

    const sheets = dataSheets.length > 0 ? dataSheets : workbook.worksheets;

    let sortBase = 0;
    for (const sheet of sheets) {
        const rows = sheetToRows(sheet);
        const prevLen = questions.length;
        extractFromRows(rows, questions, totalRespondents, sortBase);
        sortBase += questions.length - prevLen;
    }

    // Deduplicate by qid (same question might appear across sheets)
    const seen = new Set<string>();
    const unique = questions.filter((q) => {
        if (seen.has(q.qid)) return false;
        seen.add(q.qid);
        return true;
    });

    return {
        projectId: file.name.replace(/\.[^.]+$/, ''),
        surveyId: undefined,
        surveyIds: [],
        isInternalUser: false,
        questions: unique,
        totalRespondents: totalRespondents.value,
    };
}
